import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getMySubmissions, workerLogout, getDownloadUrl, deleteSubmission } from '../api';
import { formatDateEST } from '../utils/dateUtils';
import './WorkerDashboard.css';

/**
 * Worker Dashboard Component
 * Shows worker's own submissions with search, filter, sort, and pagination
 */
function WorkerDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [filteredSubmissions, setFilteredSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  // Filter and search state
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'submission_date', direction: 'desc' });

  useEffect(() => {
    fetchSubmissions();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [submissions, searchTerm, dateFrom, dateTo, sortConfig, currentPage, itemsPerPage]);

  const fetchSubmissions = async () => {
    try {
      setLoading(true);
      const response = await getMySubmissions({
        page: 1,
        per_page: 1000 // Get all submissions for client-side filtering
      });

      if (response.success) {
        setSubmissions(response.submissions);
      }
    } catch (err) {
      setError(err.message || 'Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...submissions];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(sub =>
        sub.job_site?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        sub.comments?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply date range filter
    if (dateFrom) {
      filtered = filtered.filter(sub => new Date(sub.submission_date) >= new Date(dateFrom));
    }
    if (dateTo) {
      filtered = filtered.filter(sub => new Date(sub.submission_date) <= new Date(dateTo));
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        // Handle date sorting
        if (sortConfig.key === 'submission_date') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        }

        // Handle numeric sorting
        if (sortConfig.key === 'file_count') {
          aVal = Number(aVal) || 0;
          bVal = Number(bVal) || 0;
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    // Calculate pagination
    const total = Math.ceil(filtered.length / itemsPerPage);
    setTotalPages(total);

    // Apply pagination
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginated = filtered.slice(startIndex, endIndex);

    setFilteredSubmissions(paginated);
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleResetFilters = () => {
    setSearchTerm('');
    setDateFrom('');
    setDateTo('');
    setSortConfig({ key: 'submission_date', direction: 'desc' });
    setCurrentPage(1);
  };

  const handleLogout = async () => {
    try {
      await workerLogout();
      localStorage.removeItem('worker_user');
      if (onLogout) {
        onLogout();
      }
      navigate('/worker/login');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };


  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <span className="sort-icon-neutral">⇅</span>;
    }
    return sortConfig.direction === 'asc' ?
      <span className="sort-icon-asc">↑</span> :
      <span className="sort-icon-desc">↓</span>;
  };

  const handleDeleteSubmission = async (id) => {
    if (!window.confirm('Are you sure you want to delete this submission? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await deleteSubmission(id);
      if (response.success) {
        alert('Submission deleted successfully');
        // Refresh submissions list
        fetchSubmissions();
      }
    } catch (error) {
      alert('Failed to delete submission: ' + error.message);
    }
  };

  return (
    <div className="worker-dashboard-page">
      {/* Header */}
      <header className="worker-dashboard-header">
        <div className="worker-dashboard-header-container">
          <div className="brand">
            <img src="/logo-small.png" alt="SSM" className="logo" />
            <span className="brand-name">Worker Portal</span>
          </div>
          <div className="header-actions">
            <span className="user-greeting">Hello, {user?.full_name || 'Worker'}</span>
            <button onClick={() => navigate('/')} className="btn-home">
              <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
              <span>Home</span>
            </button>
            <button onClick={handleLogout} className="btn-logout">
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="worker-dashboard-main">
        <div className="worker-dashboard-container">
          {/* Welcome Section */}
          <div className="welcome-section">
            <h1 className="dashboard-title">My Submissions</h1>
            <p className="dashboard-subtitle">
              View your submission history and upload new forms
            </p>
          </div>

          {/* Quick Actions */}
          <div className="quick-actions">
            <button
              className="action-btn primary"
              onClick={() => navigate('/upload-form')}
            >
              <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <span>Upload New Form</span>
            </button>
            <button
              className="action-btn secondary"
              onClick={() => navigate('/documents')}
            >
              <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              <span>Download Templates</span>
            </button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="alert alert-error">
              <div className="alert-content">
                ✕ {error}
              </div>
              <button
                className="alert-close"
                onClick={() => setError('')}
              >
                ×
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="loading-container">
              <div className="spinner-large"></div>
              <p>Loading submissions...</p>
            </div>
          )}

          {/* Submissions Table */}
          {!loading && submissions.length === 0 && (
            <div className="empty-state">
              <p>No submissions yet. Start by uploading your first form!</p>
              <button
                className="btn-primary"
                onClick={() => navigate('/upload-form')}
              >
                Upload/Submit
              </button>
            </div>
          )}

          {!loading && submissions.length > 0 && (
            <div className="submissions-section">
              {/* Filters Section */}
              <div className="filters-section">
                <div className="filter-row">
                  <div className="filter-group">
                    <label htmlFor="search">Search</label>
                    <input
                      id="search"
                      type="text"
                      placeholder="Search by job site or comments..."
                      value={searchTerm}
                      onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="filter-input"
                    />
                  </div>
                  <div className="filter-group">
                    <label htmlFor="dateFrom">From Date</label>
                    <input
                      id="dateFrom"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="filter-input"
                    />
                  </div>
                  <div className="filter-group">
                    <label htmlFor="dateTo">To Date</label>
                    <input
                      id="dateTo"
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setCurrentPage(1);
                      }}
                      className="filter-input"
                    />
                  </div>
                  <div className="filter-group">
                    <label htmlFor="itemsPerPage">Per Page</label>
                    <select
                      id="itemsPerPage"
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="filter-input"
                    >
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="20">20</option>
                      <option value="50">50</option>
                    </select>
                  </div>
                  <div className="filter-actions">
                    <button
                      onClick={handleResetFilters}
                      className="btn-reset"
                    >
                      Reset Filters
                    </button>
                  </div>
                </div>
                <div className="filter-info">
                  <p>Showing {filteredSubmissions.length} of {submissions.length} submissions</p>
                </div>
              </div>

              {/* Table */}
              <div className="table-container">
                <table className="submissions-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('submission_date')} className="sortable">
                        Date {getSortIcon('submission_date')}
                      </th>
                      <th onClick={() => handleSort('job_site')} className="sortable">
                        Job Site {getSortIcon('job_site')}
                      </th>
                      <th onClick={() => handleSort('file_count')} className="sortable">
                        Files {getSortIcon('file_count')}
                      </th>
                      <th>Comments</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSubmissions.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="no-results">
                          No submissions found matching your filters.
                        </td>
                      </tr>
                    ) : (
                      filteredSubmissions.map((submission) => (
                        <tr key={submission.id}>
                          <td>{formatDateEST(submission.submission_date)}</td>
                          <td>{submission.job_site}</td>
                          <td>
                            <span className="file-count-badge">
                              {submission.file_count} file(s)
                            </span>
                          </td>
                          <td className="comments-cell">
                            {submission.comments
                              ? submission.comments.length > 50
                                ? submission.comments.substring(0, 50) + '...'
                                : submission.comments
                              : '-'}
                          </td>
                          <td>
                            <div className="action-buttons">
                              {submission.files && submission.files.length > 0 && (
                                submission.files.map((file) => (
                                  <a
                                    key={file.id}
                                    href={getDownloadUrl(file.id)}
                                    className="btn-download"
                                    title={`Download ${file.file_name}`}
                                  >
                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                                      <polyline points="7 10 12 15 17 10"/>
                                      <line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                  </a>
                                ))
                              )}
                              <button
                                onClick={() => handleDeleteSubmission(submission.id)}
                                className="btn-delete"
                                title="Delete this submission"
                              >
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <polyline points="3 6 5 6 21 6"/>
                                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                  <line x1="10" y1="11" x2="10" y2="17"/>
                                  <line x1="14" y1="11" x2="14" y2="17"/>
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="btn-page"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    ← Previous
                  </button>
                  <span className="page-info">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="btn-page"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="worker-dashboard-footer">
        <div className="footer-content">
          <p>
            <svg className="footer-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
            </svg>
            Need help? Call us at <a href="tel:+12124988863">+1 (212)-498-8863</a>
          </p>
          <p>© 2025 Skilled Safety Management. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default WorkerDashboard;
